import { motion } from "framer-motion";
import { MapPin } from "lucide-react";

const ZONES = [
  {
    name: "Northern Zone",
    suburbs: ["Wanneroo", "Pearsall", "Hocking", "Darch", "Gnangara", "Joondalup", "Connolly", "Edgewater", "Currambine", "Iluka", "Burns Beach", "Kinross", "Clarkson", "Butler", "Alkimos", "Eglinton", "Carine", "Duncraig", "Kingsley", "Greenwood", "Hillarys", "Sorrento", "Padbury", "Beldon", "Craigie"],
  },
  {
    name: "Central & Western Zone",
    suburbs: ["Nedlands", "Dalkeith", "Claremont", "Cottesloe", "Mosman Park", "Peppermint Grove", "Crawley", "Perth", "Northbridge", "Highgate", "Leederville", "Mt Lawley", "West Perth", "East Perth", "Subiaco", "Jolimont", "South Perth", "Como", "Kensington", "Victoria Park", "Burswood"],
  },
  {
    name: "Fremantle & Melville Zone",
    suburbs: ["Applecross", "Ardross", "Attadale", "Booragoon", "Brentwood", "Bull Creek", "Melville", "Alfred Cove", "Bicton", "Palmyra", "Fremantle", "East Fremantle", "South Fremantle", "Beaconsfield", "Hilton", "O'Connor", "North Fremantle"],
  },
  {
    name: "Southern Corridor",
    note: "Cockburn · Kwinana · Rockingham",
    suburbs: ["Bibra Lake", "Coolbellup", "Hamilton Hill", "Spearwood", "Coogee", "Cockburn Central", "Atwell", "Aubin Grove", "Hammond Park", "Beeliar", "Wandi", "Bertram", "Kwinana Town Centre", "Calista", "Leda", "Medina", "Baldivis", "Cooloongup", "Rockingham", "Safety Bay", "Warnbro", "Secret Harbour", "Golden Bay", "Karnup"],
  },
  {
    name: "Peel Zone",
    suburbs: ["Lakelands", "Madora Bay", "Mandurah", "Halls Head", "Greenfields", "Dawesville", "Falcon"],
  },
];

const ServiceAreaSection = () => (
  <section id="service-area" data-testid="service-area-section" className="relative py-20 sm:py-28 lg:py-36 bg-surface/40 border-y border-edge">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-volt mb-4">
          05 / Service Area
        </div>
        <h2 className="font-syne font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-100 leading-tight max-w-3xl">
          Wanneroo to Serpentine. <span className="text-outline">We come to you.</span>
        </h2>
        <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl">
          Fully mobile across the Perth metro — from Wanneroo in the north, through the city and Fremantle,
          down the southern corridor to Rockingham and the Peel region.
        </p>
      </motion.div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ZONES.map((zone, i) => (
          <motion.div
            key={zone.name}
            data-testid={`zone-card-${zone.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="group bg-panel border border-edge rounded-xl p-6 hover:border-volt/60 hover:shadow-[0_0_24px_rgba(0,240,255,0.12)] transition-[border-color,box-shadow] duration-300"
          >
            <div className="flex items-center gap-2.5 mb-1">
              <MapPin size={15} className="text-volt shrink-0" />
              <h3 className="font-syne font-bold uppercase tracking-wider text-base text-slate-100 group-hover:text-volt transition-colors duration-300">
                {zone.name}
              </h3>
            </div>
            {zone.note && (
              <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-1 ml-6">{zone.note}</div>
            )}
            <div className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 mb-4 ml-6">
              {zone.suburbs.length} suburbs
            </div>
            <div className="flex flex-wrap gap-1.5">
              {zone.suburbs.map((s) => (
                <span
                  key={s}
                  className="font-mono2 text-[10px] tracking-wide text-slate-400 border border-edge/70 rounded-full px-2.5 py-1 group-hover:border-edge group-hover:text-slate-300 transition-colors duration-300"
                >
                  {s}
                </span>
              ))}
            </div>
          </motion.div>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="bg-carbon border border-volt/30 rounded-xl p-6 flex flex-col justify-center glow-volt"
        >
          <div className="font-syne font-bold uppercase tracking-wider text-lg text-slate-100">
            Just outside the list?
          </div>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">
            If you're near one of these zones, chances are we can still get to you.
            Call <a href="tel:0459440803" data-testid="service-area-call-link" className="text-volt hover:underline">0459 440 803</a> and we'll confirm.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);

export default ServiceAreaSection;
